// Memoized per locale|currency — Intl.NumberFormat construction is expensive
// and price lists render dozens of values with the same formatter.
const formatters = new Map<string, Intl.NumberFormat>()

/**
 * Format a number as currency using Intl.NumberFormat
 *
 * @param amount - The numeric amount to format
 * @param currency - ISO 4217 currency code (e.g., 'EUR', 'USD')
 * @param locale - BCP 47 locale string (e.g., 'de-DE', 'en-IE') or null for default
 * @param maxFractionDigits - Raise the cap above the currency default (2 for
 *   EUR) for rates that are a fraction of a cent, e.g. an hourly price. The
 *   minimum stays at the currency default, so ordinary amounts are unchanged.
 * @returns Formatted currency string
 */
export const formatCurrency = (
  amount: number,
  currency: string,
  locale: string | null,
  maxFractionDigits?: number
): string => {
  const key = `${locale ?? ''}|${currency}|${maxFractionDigits ?? ''}`
  let formatter = formatters.get(key)
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale || undefined, {
      currency,
      style: 'currency',
      ...(maxFractionDigits === undefined ? {} : { maximumFractionDigits: maxFractionDigits })
    })
    formatters.set(key, formatter)
  }

  return formatter.format(amount)
}

/**
 * Map an i18n locale ('de' | 'en') to the BCP 47 locale used for price display.
 * en-IE is the chosen English convention (EUR-native English formatting),
 * shared across all landing/product pages.
 */
export const currencyLocale = (i18nLocale: string): string =>
  i18nLocale === 'de' ? 'de-DE' : 'en-IE'

/**
 * Format a EUR amount according to the current i18n locale ('de' | 'en').
 */
export const formatEur = (amount: number, i18nLocale: string): string =>
  formatCurrency(amount, 'EUR', currencyLocale(i18nLocale))
